
/* eslint-disable vue/no-mutating-props --
 * JoinConfigSection is an in-place editor on the modal's reactive joinConfig / parents
 * state. Each primitive input is bound with v-model on a prop property, which Vue expands
 * to a direct assignment on the prop — intentional, since the parent reads state.joinConfig
 * directly in its save path. Rewriting every input to emit('update:joinConfig', <clone>)
 * would multiply the code without changing behaviour.
 *
 * FRAGILE for any new caller: this only works because the parent passes a single reactive
 * object (`state.joinConfig`) that it mutates in place and reads back from the same
 * reference. Pass a cloned prop in and the child's writes won't propagate. If a second
 * caller shows up, lift the state up or convert to emit('update:*') before reusing this
 * component — same restriction applies to TagMultiPicker and TagSinglePicker.
 */
import { defineComponent, PropType } from "vue";
import VueFeather from "vue-feather";
import TagMultiPicker from "./TagMultiPicker.vue";
import TagSinglePicker from "./TagSinglePicker.vue";
import {
  JoinConfigDraft,
  ParentDraft,
  TagActionRowDraft,
  TagSetDraft,
  TAG_ACTION_OPTIONS,
  TAG_ACTION_TITLES,
} from "./tasksConfigTypes";
import { TaskRule } from "@/interfaces/TaskRule";

export default defineComponent({
  name: "JoinConfigSection",
  components: { VueFeather, TagMultiPicker, TagSinglePicker },
  props: {
    parents: {
      type: Array as PropType<ParentDraft[]>,
      required: true,
    },
    joinConfig: {
      type: Object as PropType<JoinConfigDraft>,
      required: true,
    },
    // Rule-wide shared sensor tags (same reactive array the modal uses for
    // non-join rules — mutated in place by the shared TagMultiPicker).
    tagSets: {
      type: Array as PropType<TagSetDraft[]>,
      required: true,
    },
    sensorTagOptions: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    taskRules: {
      type: Array as PropType<TaskRule[]>,
      required: true,
    },
  },
  setup(props) {
    const parentDisplayName = (p: ParentDraft): string => {
      if (p.kind === "task_rule") {
        if (p.task_rule_id === null) return "unassigned task_rule";
        const found = props.taskRules.find((r) => r.TaskRuleID === p.task_rule_id);
        return found ? found.Name : `Rule ${p.task_rule_id}`;
      }
      return p.kind;
    };

    const otherParentIds = (p: ParentDraft): number[] =>
      props.parents.filter((x) => x !== p).map((x) => x.id);

    const addParentTagSet = (p: ParentDraft) => {
      p.tagSets.push({ tags: {}, selectTag: "", selectTagValue: "" });
    };

    const addParentTagAction = (p: ParentDraft) => {
      p.tagActions.push({ tag: "", action: "combine", pn: "" });
    };

    const removeParentTagAction = (p: ParentDraft, index: number) => {
      p.tagActions.splice(index, 1);
    };

    // When the user flips the action away from Match, drop the (now-meaningless) pn so
    // an invalid stale target can't reach save-time validation.
    const onTagActionChange = (row: TagActionRowDraft) => {
      if (row.action !== "match") row.pn = "";
    };

    // Tag keys valid for one Sensor Tags Broadcast row's Tag column. Excludes tags in
    // filter_to_same (redundant with the shared setting — same rule the save-time
    // validator enforces) and tags already picked by other rows on this parent (a
    // duplicate would fail validation). Keeps the row's own current pick so an existing
    // selection stays visible even if it'd otherwise be filtered out (e.g. a legacy tag
    // no longer in sensorTagOptions).
    const availableBroadcastTagsFor = (parent: ParentDraft, row: TagActionRowDraft): string[] => {
      const takenByOtherRows = new Set(
        parent.tagActions.filter((r) => r !== row && r.tag).map((r) => r.tag)
      );
      const inFilterToSame = new Set(props.joinConfig.filter_to_same);
      const opts = new Set(
        Object.keys(props.sensorTagOptions).filter(
          (t) => !takenByOtherRows.has(t) && !inFilterToSame.has(t)
        )
      );
      if (row.tag) opts.add(row.tag);
      return Array.from(opts).sort();
    };

    const updateFilterToSame = (next: string[]) => {
      props.joinConfig.filter_to_same = next;
    };

    const filterToSameHelp =
      "Match ALL parents based on these tags. e.g. parent tasks x and y, each " +
      "with `filter_to_same = 'Blade'` spawn child tasks with inputs (xA, yA), " +
      "(xB, yB), and (xC, yC), WITHOUT mixed-blade children like (xA, yB)." +
      "A tag listed here must not appear in any parent's Sensor Tags Broadcast " +
      "(it's redundant).";

    return {
      tagActionOptions: TAG_ACTION_OPTIONS,
      tagActionTitles: TAG_ACTION_TITLES,
      parentDisplayName,
      otherParentIds,
      addParentTagSet,
      addParentTagAction,
      removeParentTagAction,
      onTagActionChange,
      availableBroadcastTagsFor,
      updateFilterToSame,
      filterToSameHelp,
    };
  },
});
